import useApiRequest from "@/lib/hooks/use-request";
import { PipelineSelectableEntityResponseDto } from "@/lib/interfaces/pipelines";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import LinkedItemManager from "@/ui-components/ui/linked-item-manager";
import { useCallback } from "react";

type PipelineLinkedProps = {
  onAssign: (pipelineId: string, title: string) => Promise<void>;
} & (
  | {
      multiple: true;
      item: PipelineSelectableEntityResponseDto[];
      onRemove: (id: string) => Promise<void>;
    }
  | {
      multiple?: false;
      item?: PipelineSelectableEntityResponseDto;
      onRemove?: () => Promise<void>;
    }
);

const PipelineLinked = ({ item, onAssign, onRemove, multiple = false }: PipelineLinkedProps) => {
  const { apiRequest } = useApiRequest();

  const fetchSearchResults = useCallback(
    async (pagination: Pagination, searchText: string): Promise<Paginated<PipelineSelectableEntityResponseDto>> => {
      try {
        let url = `pipelines/search?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
        if (searchText) {
          url = `${url}&search=${searchText}`;
        }
        const response = await apiRequest<Paginated<PipelineSelectableEntityResponseDto>>(url, "GET");

        if (!response.data) {
          return {
            docs: [],
            hasNextPage: false,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
            page: pagination.pageIndex,
            hasPrevPage: pagination.pageIndex > 0,
            count: 0,
            pageCount: 0,
          };
        }

        return response.data;
      } catch (error) {
        console.error("Error fetching pipelines:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [apiRequest]
  );

  const renderSearchOption = useCallback(
    (pipeline: PipelineSelectableEntityResponseDto) => ({
      label: `${pipeline.title}`,
      value: pipeline._id,
    }),
    []
  );

  if (multiple) {
    return (
      <>
        {Array.isArray(item) &&
          item.length > 0 &&
          item.map((pipeline) => (
            <div key={pipeline._id} className="py-1 border-b last:border-b-0">
              <LinkedItemManager<PipelineSelectableEntityResponseDto>
                fetchSearchResults={fetchSearchResults}
                renderSearchOption={renderSearchOption}
                customOnSubmit={onAssign}
                removeItem={onRemove}
                link={{
                  title: pipeline.title,
                  href: `/pipelines/${pipeline._id}`,
                  id: pipeline._id,
                }}
                title="Pipeline"
              />
            </div>
          ))}
        <div className="py-1 border-b last:border-b-0">
          <LinkedItemManager<PipelineSelectableEntityResponseDto>
            fetchSearchResults={fetchSearchResults}
            renderSearchOption={renderSearchOption}
            customOnSubmit={onAssign}
            removeItem={onRemove}
            title="Pipeline"
            multiple={true}
          />
        </div>
      </>
    );
  }

  return (
    <LinkedItemManager<PipelineSelectableEntityResponseDto>
      fetchSearchResults={fetchSearchResults}
      renderSearchOption={renderSearchOption}
      customOnSubmit={onAssign}
      removeItem={onRemove}
      link={
        item && !Array.isArray(item)
          ? {
              title: `${item.title}`,
              href: `/pipelines/${item._id}`,
              id: item._id,
            }
          : undefined
      }
      title="Pipeline"
    />
  );
};

export default PipelineLinked;
